import { Component, EventEmitter, Input, Output } from '@angular/core';
import { TSelectOption } from 'src/app/project/components/input/select/select.model';
import { EAdvancedFilterOptions } from '../../../../models/advanced-filter-options.enum';
import { EAdvancedFilterTimelineDate } from '../../../../models/advanced-filter-timeline-date.enum';
import {
  TAdvancedFilterSingle,
  TAdvancedFilterTimeline,
  TFilterTimelineOptions,
} from '../../../../models/advanced-filter.model';
import {
  advancedTimelineDatesOperatorOptionsExtended,
  IS_EMPTY_NUMERICAL,
  IS_NOT_EMPTY_NUMERICAL,
  timelineOperatorOptionsExtended,
} from '../../../../utils/advanced-filter-operator.utils';

const TIMELINE_DATE_TO_OPTION: Partial<
  Record<EAdvancedFilterTimelineDate, TFilterTimelineOptions>
> = {
  [EAdvancedFilterTimelineDate.IS_BETWEEN]: EAdvancedFilterOptions.IS_BETWEEN,
  [EAdvancedFilterTimelineDate.IS_EMPTY]: EAdvancedFilterOptions.IS_EMPTY,
  [EAdvancedFilterTimelineDate.IS_NOT_EMPTY]: EAdvancedFilterOptions.IS_NOT_EMPTY,
};

@Component({
  selector: 'pp-advanced-filter-timeline',
  templateUrl: './advanced-filter-timeline.component.html',
  styleUrls: ['./advanced-filter-timeline.component.scss'],
  standalone: false,
})
export class AdvancedFilterTimelineComponent {
  @Input() ppFilter: TAdvancedFilterSingle;
  @Output() ppFilterChange = new EventEmitter<TAdvancedFilterSingle>();

  option: TFilterTimelineOptions;
  filter: TAdvancedFilterTimeline;

  startDate: number;
  endDate: number;
  selectedDate: EAdvancedFilterTimelineDate;

  advancedFilterTimelineOptions: TSelectOption[];
  advancedFilterTimelineDates: TSelectOption[];
  EAdvancedFilterTimelineDate = EAdvancedFilterTimelineDate;
  EAdvancedFilterOptions = EAdvancedFilterOptions;

  ngOnChanges(): void {
    this.advancedFilterTimelineOptions = timelineOperatorOptionsExtended;
    this.advancedFilterTimelineDates = advancedTimelineDatesOperatorOptionsExtended;

    if (this.ppFilter && !this.ppFilter?.value) {
      this.ppFilter.value = [null, null];
    }

    this.filter = this.ppFilter as TAdvancedFilterTimeline;

    if (
      this.filter.date !== EAdvancedFilterTimelineDate.IS_EMPTY &&
      this.filter.date !== EAdvancedFilterTimelineDate.IS_NOT_EMPTY &&
      !TIMELINE_DATE_TO_OPTION[this.filter.date] &&
      this.ppFilter.timelinePosition
    ) {
      this.filter.date =
        this.ppFilter.timelinePosition === 'END'
          ? EAdvancedFilterTimelineDate.END_DATE
          : EAdvancedFilterTimelineDate.START_DATE;
    }

    this.startDate = this.filter.value?.[0] ? Number(this.filter.value[0]) : null;
    this.endDate = this.filter.value?.[1] ? Number(this.filter.value[1]) : null;
    this.option = TIMELINE_DATE_TO_OPTION[this.filter.date] ?? this.filter.option;
    this.selectedDate = this.filter.date;
  }

  changeFieldCondition(newCondition: TSelectOption): void {
    this.filter.option = newCondition.value as TFilterTimelineOptions;
    this.option = newCondition.value as TFilterTimelineOptions;

    this.filter.operator = newCondition.operator;

    if (
      newCondition.value === EAdvancedFilterOptions.IS_EMPTY ||
      newCondition.value === EAdvancedFilterOptions.IS_NOT_EMPTY
    ) {
      const option =
        newCondition.value === EAdvancedFilterOptions.IS_EMPTY
          ? IS_EMPTY_NUMERICAL
          : IS_NOT_EMPTY_NUMERICAL;
      this.filter.operator = option.operator;
      this.filter.value = option.data as (number | string)[];
    }

    this.applyChanges();
  }

  changeFieldDate(newDate: TSelectOption): void {
    this.filter.date = newDate.value as EAdvancedFilterTimelineDate;
    this.selectedDate = newDate.value as EAdvancedFilterTimelineDate;

    const mappedOption: TFilterTimelineOptions = TIMELINE_DATE_TO_OPTION[this.filter.date];

    if (mappedOption) {
      this.option = mappedOption;
      this.filter.option = mappedOption;
    }

    this.filter.timelinePosition = newDate.timelinePosition ?? null;

    if (!this.filter.operator || mappedOption) {
      this.filter.operator = newDate.operator;
    }

    this.checkEmptyValues(newDate.value);

    this.applyChanges();
  }

  private checkEmptyValues(value: string): void {
    if (
      value === EAdvancedFilterTimelineDate.IS_EMPTY ||
      value === EAdvancedFilterTimelineDate.IS_NOT_EMPTY
    ) {
      const option =
        this.filter.date === EAdvancedFilterTimelineDate.IS_EMPTY
          ? IS_EMPTY_NUMERICAL
          : IS_NOT_EMPTY_NUMERICAL;
      this.filter.operator = option.operator;
      this.filter.value = option.data as (number | string)[];
    }
  }

  applyChanges(): void {
    const isEmptyOption =
      this.filter.option === EAdvancedFilterOptions.IS_EMPTY ||
      this.filter.option === EAdvancedFilterOptions.IS_NOT_EMPTY;

    if (!isEmptyOption && this.filter.value?.every((v) => v === null)) {
      this.filter.value = null;
    }

    this.ppFilterChange.emit(this.filter);
  }

  changeFieldValue(value: Date[], index: number): void {
    const date = value[0];
    let timestamp: number;

    timestamp = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());

    this.filter.value[index] = timestamp;

    switch (index) {
      case 0:
        this.startDate = timestamp;
        break;
      case 1:
        this.endDate = timestamp;
        break;
    }
    this.applyChanges();
  }
}
